"use client";

import { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import {
  addToCart,
  findByIDCart,
  deleteCart,
  findAvailableTicket,
  findAllAvailableHotelRoomPrice,
  updateTicketQuantity,
  updateHotelQuantity,
} from "@/services/cart.services";
import { fetchAllActiveTickets } from "@/services/ticket.services";
import { fetchHotelsForCartModal } from "@/services/hotel.services";
import {
  setCartItems,
  setHotelCart,
  setStep,
  setLoading,
  setTicketDate,
} from "@/store/cartSlice";
import {
  Calendar,
  Clock,
  ChevronRight,
  ChevronLeft,
  ShoppingCart,
  Users,
  Plus,
  Minus,
  X,
  Info,
  ShieldAlert,
  MapPin,
  Loader2,
} from "lucide-react";
import Image from "next/image";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "motion/react";
import dynamic from "next/dynamic";

const AttractionModal = dynamic(() => import("./AttractionModal"), {
  ssr: false,
});
const HotelModal = dynamic(() => import("./HotelModal"), {
  ssr: false,
});
const ConfirmationModal = dynamic(() => import("./ConfirmationModal"), {
  ssr: false,
});
import {
  formatIndianNumber,
  decodeHtml,
  cleanHeaderTags,
} from "@/_helper/helper";
import { useRouter } from "next/navigation";
import { MdDelete } from "react-icons/md";
import { BiDetail } from "react-icons/bi";

function ChildAgeModal({
  isOpen,
  onClose,
  onDone,
  initialAges,
  maxExtraSlots,
  tillChildAge = 6,
}: {
  isOpen: boolean;
  onClose: () => void;
  onDone: (ages: number[]) => void;
  initialAges: number[];
  maxExtraSlots: number;
  tillChildAge?: number;
}) {
  const [ages, setAges] = useState<number[]>([]);

  useEffect(() => {
    if (isOpen) setAges(initialAges.length > 0 ? [...initialAges] : []);
  }, [isOpen, initialAges]);

  if (!isOpen) return null;

  const paidCount = ages.filter((a) => a > tillChildAge).length;

  const addChild = () => {
    setAges((prev) => [...prev, tillChildAge]);
  };

  const removeChild = (idx: number) => {
    setAges((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateAge = (idx: number, delta: number) => {
    setAges((prev) =>
      prev.map((age, i) => {
        if (i === idx) {
          const newAge = Math.min(12, Math.max(1, age + delta));
          return newAge;
        }
        return age;
      }),
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
      />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="relative bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden"
      >
        <div className="p-6 sm:p-8 space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl sm:text-2xl font-bold text-orange-600">
                Extra Children
              </h3>
              <p className="text-xs text-gray-400 font-medium mt-1">
                Ages 1-12 years
              </p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-orange-500 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="bg-orange-50/50 rounded-2xl p-4 border border-orange-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="text-xs font-bold text-orange-600 uppercase">
                Policy
              </p>
              <p className="text-[10px] text-gray-600 font-medium">
                • Upto {tillChildAge} years: Free (No Extra Bed)
                <br />• {tillChildAge + 1}-12 years: Paid (counts in occupancy)
              </p>
            </div>
            <div className="text-left sm:text-right">
              <p className="text-[10px] font-bold text-gray-400 uppercase">
                Paid Count
              </p>
              <p className="text-lg font-bold text-gray-800">
                {paidCount} / {maxExtraSlots}
              </p>
            </div>
          </div>

          <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
            {ages.map((age, idx) => (
              <div
                key={idx}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white rounded-2xl border border-gray-100 hover:border-orange-200 transition-all group"
              >
                <div className="space-y-0.5">
                  <span className="text-xs font-bold text-gray-800">
                    Child {idx + 1}
                  </span>
                  <p
                    className={`text-[10px] font-bold ${age <= tillChildAge ? "text-green-500" : "text-orange-500"}`}
                  >
                    {age <= tillChildAge ? "FREE" : "PAID"}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-3 bg-gray-50 p-1 rounded-md">
                    <button
                      onClick={() => updateAge(idx, -1)}
                      className="w-7 h-7 rounded-md bg-white shadow-sm flex items-center justify-center text-gray-400 hover:text-orange-500 transition-all"
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="w-6 text-center text-sm font-bold text-gray-800">
                      {age}
                    </span>
                    <button
                      disabled={
                        age === tillChildAge && paidCount >= maxExtraSlots
                      }
                      onClick={() => updateAge(idx, 1)}
                      className="w-7 h-7 rounded-md bg-white shadow-sm flex items-center justify-center text-gray-400 hover:text-orange-500 transition-all disabled:opacity-20 disabled:cursor-not-allowed"
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <button
                    onClick={() => removeChild(idx)}
                    className="w-8 h-8 rounded-full flex items-center justify-center text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}

            {ages.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-400 font-medium">
                  No children added yet
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={addChild}
              className="flex-1 py-3.5 border-2 border-dashed border-gray-200 text-gray-400 font-bold rounded-2xl hover:border-orange-500 hover:text-orange-500 transition-all text-sm flex items-center justify-center gap-2 disabled:opacity-30 disabled:cursor-not-allowed"
            >
              <Plus className="w-4 h-4" /> Add Child
            </button>
            <button
              onClick={() => {
                onDone(ages);
                onClose();
              }}
              className="flex-1 py-3.5 bg-orange-500 text-white font-bold rounded-2xl shadow-lg hover:bg-orange-600 transition-all text-sm"
            >
              Done
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// Custom Date Range Picker Component
function DateRangePicker({
  startDate,
  endDate,
  onRangeSelect,
}: {
  startDate: string;
  endDate: string;
  onRangeSelect: (start: string, end: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(new Date(startDate || new Date()));
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) =>
    new Date(year, month, 1).getDay();

  const handleDateClick = (dateStr: string) => {
    if (!startDate || (startDate && endDate)) {
      onRangeSelect(dateStr, "");
    } else {
      if (new Date(dateStr) < new Date(startDate)) {
        onRangeSelect(dateStr, "");
      } else {
        onRangeSelect(startDate, dateStr);
        setIsOpen(false);
      }
    }
  };

  const isSelected = (dateStr: string) =>
    dateStr === startDate || dateStr === endDate;
  const isInRange = (dateStr: string) => {
    if (!startDate) return false;
    const date = new Date(dateStr);
    const start = new Date(startDate);

    if (endDate) {
      const end = new Date(endDate);
      return date > start && date < end;
    }

    if (hoverDate) {
      const hover = new Date(hoverDate);
      return hover > start ? date > start && date < hover : false;
    }

    return false;
  };

  const isPast = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateObj < today;
  };

  const renderCalendar = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const past = isPast(dateStr);
      const selected = isSelected(dateStr);
      const inRange = isInRange(dateStr);

      days.push(
        <button
          key={dateStr}
          disabled={past}
          onMouseEnter={() => !past && setHoverDate(dateStr)}
          onMouseLeave={() => setHoverDate(null)}
          onClick={() => handleDateClick(dateStr)}
          className={`h-10 w-full flex items-center justify-center text-sm font-semibold rounded-2xl transition-all relative
            ${selected ? "bg-orange-500 text-white shadow-lg z-10" : ""}
            ${inRange ? "bg-orange-50 text-orange-600 rounded-none first:rounded-l-2xl last:rounded-r-2xl" : ""}
            ${!selected && !inRange && !past ? "hover:bg-gray-100 text-gray-700" : ""}
            ${past ? "text-gray-200 cursor-not-allowed" : "cursor-pointer"}
          `}
        >
          {d}
          {dateStr === startDate && !endDate && (
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 bg-white rounded-full"></span>
          )}
        </button>,
      );
    }
    return days;
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      className={`relative ${isOpen ? "z-[1000]" : "z-[1]"}`}
      ref={containerRef}
    >
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="group relative flex items-center bg-orange-50/40 rounded-2xl border border-orange-100 hover:border-orange-200 hover:bg-orange-50/60 transition-all duration-300 shadow-sm cursor-pointer overflow-hidden p-1"
      >
        <div className="flex-1 flex flex-col sm:flex-row items-stretch sm:items-center justify-between">
          <div className="flex-1 p-2.5 sm:p-3.5 pl-4 sm:pl-4 pr-4 sm:pr-0 min-w-0 flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-start w-full sm:w-auto gap-2 sm:gap-1">
            <p className="text-[10px] sm:text-xs font-bold text-orange-400 uppercase tracking-widest flex items-center gap-1.5 shrink-0">
              <Calendar className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">Check-in</span>
            </p>
            <p className="text-xs sm:text-base font-bold text-gray-800 truncate">
              {startDate
                ? startDate.split("-").reverse().join("/")
                : "Select Date"}
            </p>
          </div>

          <div className="relative flex items-center justify-center py-2 sm:py-2 px-4 sm:px-4 shrink-0 w-full sm:w-auto">
            <div className="hidden sm:block w-px h-8 bg-orange-200/50"></div>
            <div className="block sm:hidden absolute left-4 right-4 h-px bg-orange-200/50"></div>
            <div className="relative sm:absolute sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 px-2.5 sm:px-3 py-1 bg-gradient-to-r from-orange-500 to-amber-500 text-white rounded-full text-[9px] sm:text-[10px] font-bold shadow-lg shadow-orange-500/20 whitespace-nowrap z-10 border border-white/20">
              {startDate && endDate
                ? Math.ceil(
                    (new Date(endDate).getTime() -
                      new Date(startDate).getTime()) /
                      (1000 * 60 * 60 * 24),
                  )
                : 0}{" "}
              Nights
            </div>
          </div>

          <div className="flex-1 p-2.5 sm:p-3.5 pl-4 sm:pl-0 pr-4 sm:pr-4 min-w-0 flex flex-row sm:flex-col justify-between sm:justify-start items-center sm:items-end w-full sm:w-auto gap-2 sm:gap-1">
            <p className="text-[10px] sm:text-xs font-bold text-orange-400 uppercase tracking-widest flex items-center gap-1.5 shrink-0 sm:justify-end">
              <Calendar className="w-3.5 h-3.5 shrink-0 block sm:hidden" />
              <span className="truncate">Check-out</span>
              <Calendar className="w-3.5 h-3.5 shrink-0 hidden sm:block" />
            </p>
            <p className="text-xs sm:text-base font-bold text-gray-800 truncate">
              {endDate ? endDate.split("-").reverse().join("/") : "Select Date"}
            </p>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-3xl shadow-2xl border border-gray-100 p-4 sm:p-6 z-[9999] min-w-[280px] sm:min-w-[320px]"
          >
            <div className="flex items-center justify-between mb-6">
              <button
                onClick={() =>
                  setViewDate(
                    new Date(
                      viewDate.getFullYear(),
                      viewDate.getMonth() - 1,
                      1,
                    ),
                  )
                }
                className="p-2 hover:bg-orange-50 rounded-2xl text-orange-500 transition-colors"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <h4 className="font-bold text-gray-800 text-sm sm:text-base">
                {months[viewDate.getMonth()]} {viewDate.getFullYear()}
              </h4>
              <button
                onClick={() =>
                  setViewDate(
                    new Date(
                      viewDate.getFullYear(),
                      viewDate.getMonth() + 1,
                      1,
                    ),
                  )
                }
                className="p-2 hover:bg-orange-50 rounded-2xl text-orange-500 transition-colors"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] font-bold text-gray-400 uppercase"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

            <div className="mt-6 pt-6 border-t border-gray-50 flex items-center justify-between flex-wrap gap-2">
              <div className="flex gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500"></div>
                <span className="text-[10px] font-bold text-gray-400 uppercase">
                  Selected
                </span>
              </div>
              <button
                onClick={() => {
                  onRangeSelect("", "");
                  setIsOpen(false);
                }}
                className="text-[10px] font-bold text-red-500 uppercase hover:underline"
              >
                Clear Range
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function AttractionDatePicker({
  selectedDate,
  onDateSelect,
  disabledDates = [],
}: {
  selectedDate: string;
  onDateSelect: (date: string) => void;
  disabledDates?: string[];
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [viewDate, setViewDate] = useState(
    new Date(selectedDate || new Date()),
  );
  const containerRef = useRef<HTMLDivElement>(null);

  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const getDaysInMonth = (year: number, month: number) =>
    new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (year: number, month: number) =>
    new Date(year, month, 1).getDay();

  const isPast = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-").map(Number);
    const dateObj = new Date(y, m - 1, d);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return dateObj <= today;
  };

  const isDisabled = (dateStr: string) =>
    isPast(dateStr) || disabledDates.includes(dateStr);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const renderCalendar = () => {
    const year = viewDate.getFullYear();
    const month = viewDate.getMonth();
    const daysInMonth = getDaysInMonth(year, month);
    const firstDay = getFirstDayOfMonth(year, month);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10"></div>);
    }

    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
      const disabled = isDisabled(dateStr);
      const selected = dateStr === selectedDate;

      days.push(
        <button
          key={dateStr}
          type="button"
          disabled={disabled}
          onClick={() => {
            onDateSelect(dateStr);
            setIsOpen(false);
          }}
          className={`h-10 w-full flex items-center justify-center text-sm font-semibold rounded-2xl transition-all relative
            ${selected ? "bg-orange-500 text-white shadow-lg z-10" : ""}
            ${!selected && !disabled ? "hover:bg-orange-50 text-gray-700 hover:text-orange-600" : ""}
            ${disabled ? "text-gray-200 cursor-not-allowed bg-gray-50/50" : "cursor-pointer"}
          `}
        >
          {d}
        </button>,
      );
    }
    return days;
  };

  return (
    <div className="relative w-full md:w-auto" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`relative flex items-center justify-between md:justify-start gap-2 px-4 py-2.5 rounded-2xl border transition-all shadow-sm group cursor-pointer w-full ${selectedDate ? "bg-orange-50 border-orange-100" : "bg-orange-50/30 border-dashed border-orange-200 ring-4 ring-orange-500/5 hover:border-orange-300"}`}
      >
        <Calendar
          className={`w-4 h-4 ${selectedDate ? "text-orange-600" : "text-orange-400"} group-hover:scale-110 transition-transform shrink-0`}
        />
        <span
          className={`font-semibold text-sm ${selectedDate ? "text-orange-600" : "text-orange-500"} truncate flex-1 text-left`}
        >
          {selectedDate
            ? selectedDate.split("-").reverse().join("-")
            : "Select Visit Date"}
        </span>
        <ChevronRight
          className={`w-3.5 h-3.5 transition-transform ${isOpen ? "rotate-[-90deg]" : "rotate-90"} text-orange-400 shrink-0`}
        />
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute top-full left-0 right-0 md:left-auto md:right-0 mt-2 bg-white rounded-3xl shadow-2xl border border-orange-50 p-4 sm:p-6 z-[100] w-full md:min-w-[300px]"
          >
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() =>
                  setViewDate(
                    new Date(
                      viewDate.getFullYear(),
                      viewDate.getMonth() - 1,
                      1,
                    ),
                  )
                }
                className="p-1.5 hover:bg-orange-50 rounded-2xl text-orange-500 transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <h4 className="font-bold text-gray-800 text-sm">
                {months[viewDate.getMonth()]} {viewDate.getFullYear()}
              </h4>
              <button
                type="button"
                onClick={() =>
                  setViewDate(
                    new Date(
                      viewDate.getFullYear(),
                      viewDate.getMonth() + 1,
                      1,
                    ),
                  )
                }
                className="p-1.5 hover:bg-orange-50 rounded-2xl text-orange-500 transition-colors"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div
                  key={day}
                  className="text-center text-[10px] font-bold text-gray-400 uppercase"
                >
                  {day}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

            <div className="mt-4 pt-4 border-t border-gray-50 flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 rounded-full bg-gray-200"></div>
                <span className="text-[10px] font-bold text-gray-400 uppercase">
                  Unavailable
                </span>
              </div>
              <button
                type="button"
                onClick={() => {
                  onDateSelect("");
                  setIsOpen(false);
                }}
                className="text-[10px] font-bold text-red-500 uppercase hover:underline"
              >
                Clear
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const MAX_ADULT_TICKETS = 10;
const MAX_CHILD_TICKETS = 5;

const formatToYYYYMMDD = (dateStr: any) => {
  if (!dateStr) return "";

  let cleanStr = String(dateStr).trim();

  // Support ASP.NET /Date(timestamp)/ format
  if (cleanStr.includes("Date(")) {
    const match =
      cleanStr.match(/\/Date\((\d+)\)\//) || cleanStr.match(/Date\((\d+)\)/);
    if (match) {
      const timestamp = parseInt(match[1], 10);
      const d = new Date(timestamp);
      if (!isNaN(d.getTime())) {
        const year = d.getFullYear();
        const month = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      }
    }
  }

  // Extract the date portion, removing any space or T time component
  const partsForDate = cleanStr.split(/[ T]/);
  cleanStr = partsForDate[0];

  if (cleanStr.includes("-") && cleanStr.split("-")[0].length === 4) {
    return cleanStr;
  }

  if (cleanStr.includes("/")) {
    const parts = cleanStr.split("/");
    if (parts.length === 3) {
      const [d, m, y] = parts;
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
  }

  try {
    const d = new Date(cleanStr);
    if (!isNaN(d.getTime())) {
      const year = d.getFullYear();
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
  } catch (e) {}

  return dateStr;
};

function HotelCard({
  hotel,
  onRemove,
  onUpdate,
  hasDateError,
}: {
  hotel: any;
  onRemove: (id: number) => void;
  onUpdate: (data: {
    subtotal: number;
    gst: number;
    total: number;
    rooms: number;
    adults: number;
    extraAdult: number;
    child: number;
    extraChild: number;
    checkIn: string;
    checkOut: string;
    nights: number;
    availableRooms?: number;
  }) => void;
  hasDateError?: boolean;
}) {
  const [currentImg, setCurrentImg] = useState(0);
  const [isEssentialDetailsOpen, setEssentialDetailsOpen] = useState(false);
  const [isCancellationPolicyOpen, setCancellationPolicyOpen] = useState(false);
  const lastFetchedRange = useRef<string>("");
  const images = [
    hotel.HotelMainImage,
    ...(hotel.RoomCategory || []).map((rc: any) => rc.OtherImage),
  ].filter(Boolean);

  const [checkIn, setCheckIn] = useState(() => {
    const today = new Date(new Date().getTime() + 360 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    const savedCheckIn = formatToYYYYMMDD(hotel.CheckIn);
    const tomorrowOfTodayStr = new Date(
      new Date().getTime() + 24 * 60 * 60 * 1000 + 360 * 60 * 1000,
    )
      .toISOString()
      .split("T")[0];
    if (savedCheckIn === tomorrowOfTodayStr) {
      return today;
    }
    return savedCheckIn;
  });

  const [checkOut, setCheckOut] = useState(() => {
    const today = new Date(new Date().getTime() + 360 * 60 * 1000)
      .toISOString()
      .split("T")[0];
    const tomorrowStr = new Date(
      new Date(today).getTime() + 24 * 60 * 60 * 1000,
    )
      .toISOString()
      .split("T")[0];
    const savedCheckOut = formatToYYYYMMDD(hotel.CheckOut);
    const dayAfterTomorrowStr = new Date(
      new Date().getTime() + 48 * 60 * 60 * 1000 + 360 * 60 * 1000,
    )
      .toISOString()
      .split("T")[0];
    if (savedCheckOut === dayAfterTomorrowStr) {
      return tomorrowStr;
    }
    return savedCheckOut;
  });
  const [rooms, setRooms] = useState<number>(hotel.Rooms || 1);
  const [extraAdults, setExtraAdults] = useState<number>(hotel.ExtraAdult || 0);
  const [extraChildrenAges, setExtraChildrenAges] = useState<number[]>([]);
  const [isChildModalOpen, setIsChildModalOpen] = useState(false);
  const [pricingData, setPricingData] = useState<any[]>([]);
  const [availableRooms, setAvailableRooms] = useState<number>(
    hotel.AvailableRooms ?? 5,
  );
  const [loading, setLoading] = useState(false);

  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const diff = Math.ceil(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
    );
    return Math.max(0, diff);
  }, [checkIn, checkOut]);

  const maxPaxPerRoom = hotel.TotalPaxAllowed || 3;
  const baseAdults = rooms * 2;
  const totalExtraSlots = rooms * (maxPaxPerRoom - 2);

  const nextImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev + 1) % images.length);
  };
  const prevImg = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentImg((prev) => (prev - 1 + images.length) % images.length);
  };

  const calculateDetailedTotalForValues = (
    currentRooms: number,
    currentExtraAdults: number,
    currentCheckIn: string,
    currentCheckOut: string,
    currentPricingData: any[],
  ) => {
    if (!currentPricingData.length) return { subtotal: 0, gst: 0, total: 0 };
    let subtotal = 0;
    let gst = 0;

    const checkInStr = formatToYYYYMMDD(currentCheckIn);
    const checkOutStr = formatToYYYYMMDD(currentCheckOut);

    currentPricingData.forEach((day: any) => {
      const dayDateStr = formatToYYYYMMDD(day.Date);
      if (dayDateStr < checkInStr || dayDateStr >= checkOutStr) return;

      let daySubtotal = 0;
      daySubtotal += (day.CoupleCost || 0) * currentRooms;
      daySubtotal += (day.ExtraPersonCost || 0) * currentExtraAdults;
      daySubtotal += (day.ExtraChildCost || 0) * paidChildrenCount;

      const gstRate = (day.GSTPercentage || 18) / 100;
      const dayGst = daySubtotal * gstRate;

      subtotal += daySubtotal;
      gst += dayGst;
    });

    return {
      subtotal,
      gst,
      total: subtotal + gst,
    };
  };

  const handleUpdateData = (
    nextRooms: number,
    nextExtraAdults: number,
    nextCheckIn: string,
    nextCheckOut: string,
    nextPricingData: any[] = pricingData,
  ) => {
    let nextNights = 0;
    if (nextCheckIn && nextCheckOut) {
      const start = new Date(nextCheckIn);
      const end = new Date(nextCheckOut);
      const diff = Math.ceil(
        (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24),
      );
      nextNights = Math.max(0, diff);
    }

    const nextPriceDetails = calculateDetailedTotalForValues(
      nextRooms,
      nextExtraAdults,
      nextCheckIn,
      nextCheckOut,
      nextPricingData,
    );

    const checkOutStr = formatToYYYYMMDD(nextCheckOut);
    const stayData = nextPricingData.filter(
      (d: any) => formatToYYYYMMDD(d.Date) !== checkOutStr,
    );
    const minAvail =
      stayData.length > 0
        ? Math.min(...stayData.map((d: any) => d.Available ?? 0))
        : 0;

    setRooms(nextRooms);
    setExtraAdults(nextExtraAdults);
    setCheckIn(nextCheckIn);
    setCheckOut(nextCheckOut);

    onUpdate({
      ...nextPriceDetails,
      rooms: nextRooms,
      adults: nextRooms * 2 + nextExtraAdults,
      extraAdult: nextExtraAdults,
      child: extraChildrenAges.length,
      extraChild: paidChildrenCount,
      checkIn: nextCheckIn,
      checkOut: nextCheckOut,
      nights: nextNights,
      availableRooms: minAvail,
    });
  };

  const fetchData = async () => {
    if (!checkIn && !checkOut) {
      lastFetchedRange.current = "";
      setAvailableRooms(0);
      setPricingData([]);
      handleUpdateData(rooms, extraAdults, "", "", []);
      return;
    }
    if (!checkIn || !checkOut) return;
    const rangeKey = `${checkIn}_${checkOut}`;
    if (lastFetchedRange.current === rangeKey) return;
    lastFetchedRange.current = rangeKey;

    setLoading(true);
    try {
      const res = await findAllAvailableHotelRoomPrice({
        HotelID: hotel.HotelID,
        RoomCategoryID: hotel.RoomCategoryID,
        MealPlanID: hotel.MealPlanID || 0,
        FromDate: formatToYYYYMMDD(checkIn),
        ToDate: formatToYYYYMMDD(checkOut),
      });
      if (res.Status === 200) {
        const fetchedPricing = res.Data || [];
        setPricingData(fetchedPricing);

        const checkOutStr = formatToYYYYMMDD(checkOut);
        const stayData = fetchedPricing.filter(
          (d: any) => formatToYYYYMMDD(d.Date) !== checkOutStr,
        );

        const minAvail =
          stayData.length > 0
            ? Math.min(...stayData.map((d: any) => d.Available ?? 0))
            : 0;

        setAvailableRooms(minAvail);

        let initialRooms = rooms === 0 ? 1 : rooms;
        let initialExtraAdults = extraAdults;

        handleUpdateData(
          initialRooms,
          initialExtraAdults,
          checkIn,
          checkOut,
          fetchedPricing,
        );
      } else {
        toast.error("Failed to check room availability.");
        setAvailableRooms(0);
        setPricingData([]);
        handleUpdateData(0, 0, checkIn, checkOut, []);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to check room availability.");
      setAvailableRooms(0);
      setPricingData([]);
      handleUpdateData(0, 0, checkIn, checkOut, []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [checkIn, checkOut]);

  const childAgeLimit = hotel.TillChildAge || 6;
  const freeChildrenCount = extraChildrenAges.filter(
    (age) => age <= childAgeLimit,
  ).length;
  const paidChildrenCount = extraChildrenAges.filter(
    (age) => age > childAgeLimit,
  ).length;

  const calculateDetailedTotal = () => {
    if (!pricingData.length) return { subtotal: 0, gst: 0, total: 0 };
    let subtotal = 0;
    let gst = 0;

    const checkInStr = formatToYYYYMMDD(checkIn);
    const checkOutStr = formatToYYYYMMDD(checkOut);

    pricingData.forEach((day: any) => {
      const dayDateStr = formatToYYYYMMDD(day.Date);
      if (dayDateStr < checkInStr || dayDateStr >= checkOutStr) return;

      let daySubtotal = 0;
      daySubtotal += (day.CoupleCost || 0) * rooms;
      daySubtotal += (day.ExtraPersonCost || 0) * extraAdults;
      daySubtotal += (day.ExtraChildCost || 0) * paidChildrenCount;

      const gstRate = (day.GSTPercentage || 18) / 100;
      const dayGst = daySubtotal * gstRate;

      subtotal += daySubtotal;
      gst += dayGst;
    });

    return {
      subtotal,
      gst,
      total: subtotal + gst,
    };
  };

  const priceDetails = calculateDetailedTotal();
  const finalPrice = priceDetails.total;

  const handleEssentialDetailsClick = () => {
    setEssentialDetailsOpen(true);
  };

  const handleCancellationPolicyClick = () => {
    setCancellationPolicyOpen(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      id={`hotel-card-${hotel.CartID}`}
      className="bg-white rounded-3xl border border-gray-100 shadow-sm relative"
    >
      <div className="p-4 sm:p-6 md:p-8 space-y-6 md:space-y-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Image Slider */}
          <div className="relative w-full md:w-80 h-60 rounded-2xl overflow-hidden shrink-0 group">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentImg}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative w-full h-full"
              >
                <Image
                  src={images[currentImg] || "/placeholder.jpg"}
                  alt={hotel.HotelName}
                  fill
                  className="object-cover"
                />
              </motion.div>
            </AnimatePresence>

            {images.length > 1 && (
              <>
                <button
                  onClick={prevImg}
                  className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-800 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <button
                  onClick={nextImg}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center text-gray-800 sm:opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </>
            )}
          </div>
          <button
            onClick={() => onRemove(hotel.CartID)}
            className="absolute top-6 right-6 sm:top-4 sm:right-4 md:top-6 md:right-6 z-20 flex flex-col items-center gap-1 text-orange-400 hover:text-orange-600 transition-colors cursor-pointer group"
          >
            <div className="w-8 h-8 rounded-full bg-white text-orange-600 flex items-center justify-center shadow-md border border-orange-50 group-hover:bg-orange-500 group-hover:text-white transition-all">
              <MdDelete className="w-4 h-4" />
            </div>
          </button>

          <div className="flex-1 space-y-6">
            <div className="flex justify-between items-start flex-wrap gap-2">
              <div className="space-y-2">
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                  {hotel.HotelName}
                </h3>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="px-2.5 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-bold rounded-full border border-orange-100 uppercase tracking-wider">
                    {hotel.RoomCategoryName || "Standard Room"}
                  </span>
                  {hotel.MealPlanName && (
                    <span className="px-2.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold rounded-full border border-blue-100 uppercase tracking-wider">
                      {hotel.MealPlanName}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5 text-xs sm:text-sm text-gray-500 font-medium">
                  <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                  <span className="break-words">{hotel.Address}</span>
                </div>
                {hotel.MealPlanDescription && (
                  <p className="text-[11px] text-gray-400 font-medium italic">
                    Note: {hotel.MealPlanDescription}
                  </p>
                )}
              </div>
            </div>

            {/* Date Range Selector */}
            <div
              className={`rounded-2xl transition-all duration-300 ${
                hasDateError
                  ? "border border-red-500 ring-4 ring-red-100/80 shadow-[0_0_15px_rgba(239,68,68,0.15)] animate-shake"
                  : ""
              }`}
            >
              <DateRangePicker
                startDate={checkIn}
                endDate={checkOut}
                onRangeSelect={(start, end) => {
                  setCheckIn(start);
                  setCheckOut(end);
                }}
              />
            </div>

            {/* Room & Occupancy Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              <div className="p-4 bg-orange-50/20 rounded-2xl border border-orange-100/50 space-y-3 shadow-sm">
                <p className="text-xs font-bold text-gray-700">Rooms</p>
                <div className="flex items-center gap-4">
                  <button
                    disabled={rooms <= 1}
                    onClick={() => {
                      const nextRooms = Math.max(1, rooms - 1);
                      const nextExtraSlots = nextRooms * (maxPaxPerRoom - 2);

                      let updatedExtraAdults = extraAdults;
                      if (updatedExtraAdults > nextExtraSlots) {
                        updatedExtraAdults = nextExtraSlots;
                      }

                      handleUpdateData(
                        nextRooms,
                        updatedExtraAdults,
                        checkIn,
                        checkOut,
                      );
                    }}
                    className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all disabled:opacity-30"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-lg font-bold text-gray-800">
                    {rooms}
                  </span>
                  <button
                    disabled={availableRooms === null}
                    onClick={() => {
                      const nextRooms = rooms + 1;
                      handleUpdateData(
                        nextRooms,
                        extraAdults,
                        checkIn,
                        checkOut,
                      );
                    }}
                    className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                {availableRooms > 0 && (
                  <div className="text-xs text-orange-400">
                    {availableRooms} Rooms Available
                  </div>
                )}
              </div>
              <div className="p-4 bg-orange-50/20 rounded-2xl border border-orange-100/50 space-y-3 shadow-sm">
                <p className="text-xs font-bold text-gray-700">Extra Person</p>
                <div className="flex items-center gap-4">
                  <button
                    disabled={extraAdults <= 0}
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextExtraAdults = Math.max(0, extraAdults - 1);
                      handleUpdateData(
                        rooms,
                        nextExtraAdults,
                        checkIn,
                        checkOut,
                      );
                    }}
                    className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all disabled:opacity-30"
                  >
                    <Minus className="w-4 h-4" />
                  </button>
                  <span className="text-lg font-bold text-gray-800">
                    {extraAdults}
                  </span>
                  <button
                    disabled={
                      availableRooms === null ||
                      extraAdults + paidChildrenCount >= totalExtraSlots
                    }
                    onClick={(e) => {
                      e.stopPropagation();
                      const nextExtraAdults = extraAdults + 1;
                      handleUpdateData(
                        rooms,
                        nextExtraAdults,
                        checkIn,
                        checkOut,
                      );
                    }}
                    className="w-8 h-8 rounded-md bg-gray-50 flex items-center justify-center text-gray-400 hover:bg-orange-500 hover:text-white transition-all disabled:opacity-30"
                  >
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
                <p className="text-xs text-orange-400">
                  Max {totalExtraSlots} extra persons
                </p>
              </div>
              <div className="p-4 bg-orange-50/20 rounded-2xl border border-orange-100/50 space-y-3 shadow-sm">
                <p className="text-xs font-bold text-gray-700">
                  Adults (Total)
                </p>
                <span className="text-lg font-bold text-gray-800">
                  {rooms * 2 + extraAdults}
                </span>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-start justify-between gap-2">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-xs font-medium shadow-xs">
                <Info className="w-3.5 h-3.5 text-amber-600 shrink-0" />
                <span>Below {childAgeLimit} years child is complimentary</span>
              </div>
              <div>
                <div
                  onClick={handleEssentialDetailsClick}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium hover:underline cursor-pointer"
                >
                  <BiDetail className="w-3.5 h-3.5 text-amber-600 shrink-0" />{" "}
                  <span>Essential Details</span>
                </div>
                {isEssentialDetailsOpen && (
                  <AnimatePresence>
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="bg-white w-full max-w-3xl max-h-[90vh] rounded-md overflow-hidden flex flex-col shadow-2xl border border-orange-50"
                      >
                        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                          <h3 className="text-lg font-semibold text-gray-800">
                            <div className="text-lg font-semibold text-orange-600">
                              {hotel.HotelName}
                            </div>
                            <p className="text-xs">Essential Details</p>
                          </h3>
                          <button
                            onClick={() => setEssentialDetailsOpen(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-4 pt-0 space-y-2">
                          {hotel.Notes && (
                            <div
                              className="text-[11px] text-gray-500 leading-relaxed font-medium blog-content"
                              dangerouslySetInnerHTML={{
                                __html: cleanHeaderTags(
                                  decodeHtml(hotel.Notes),
                                ),
                              }}
                            />
                          )}

                          {(hotel.CheckInTime ||
                            hotel.CheckIn ||
                            hotel.CheckOutTime ||
                            hotel.CheckOut) && (
                            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 py-1">
                              {(hotel.CheckInTime || hotel.CheckIn) && (
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-orange-400" />
                                  <span className="text-[10px] font-bold text-gray-600">
                                    Check-in:{" "}
                                    {hotel.CheckInTime || hotel.CheckIn}
                                  </span>
                                </div>
                              )}
                              {(hotel.CheckOutTime || hotel.CheckOut) && (
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3 h-3 text-orange-400" />
                                  <span className="text-[10px] font-bold text-gray-600">
                                    Check-out:{" "}
                                    {hotel.CheckOutTime || hotel.CheckOut}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </motion.div>
                    </div>
                  </AnimatePresence>
                )}
                <div
                  onClick={handleCancellationPolicyClick}
                  className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-medium hover:underline cursor-pointer"
                >
                  <BiDetail className="w-3.5 h-3.5 text-amber-600 shrink-0" />{" "}
                  <span>Cancellation Policy</span>
                </div>
                {isCancellationPolicyOpen && (
                  <AnimatePresence>
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="bg-white w-full max-w-3xl max-h-[90vh] rounded-md overflow-hidden flex flex-col shadow-2xl border border-orange-50"
                      >
                        <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gray-50">
                          <h3 className="text-lg font-semibold text-gray-800">
                            <div className="text-lg font-semibold text-orange-600">
                              {hotel.HotelName}
                            </div>
                            <p className="text-xs">Cancellation Policy</p>
                          </h3>
                          <button
                            onClick={() => setCancellationPolicyOpen(false)}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <X className="w-5 h-5" />
                          </button>
                        </div>
                        <div className="p-4 pt-0 space-y-2">
                          {hotel.CancellationPolicy && (
                            <div
                              className="text-[11px] text-gray-500 leading-relaxed font-medium blog-content"
                              dangerouslySetInnerHTML={{
                                __html: decodeHtml(hotel.CancellationPolicy),
                              }}
                            />
                          )}
                        </div>
                      </motion.div>
                    </div>
                  </AnimatePresence>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer info & subtotal */}
        <div className=" border-t border-gray-50 space-y-6 md:space-y-8">
          <div className="flex justify-end">
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-8 bg-orange-50/30 px-4 sm:px-8 py-4 sm:py-5 rounded-2xl border border-orange-100/50 shadow-sm hover:shadow-md transition-shadow duration-300">
              <span className="text-xs sm:text-sm font-bold text-gray-400 uppercase tracking-widest">
                Sub Total:
              </span>
              <span className="text-2xl sm:text-3xl md:text-4xl font-bold text-orange-600 tracking-tight">
                ₹ {formatIndianNumber(finalPrice)}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

export default function Step2CartDetails({
  initialTicketId,
  initialHotelId,
  initialRoomCategoryId,
  initialMealPlanID,
}: {
  initialTicketId?: number;
  initialHotelId?: number;
  initialRoomCategoryId?: number;
  initialMealPlanID?: string;
}) {
  const dispatch = useAppDispatch();
  const router = useRouter();

  const { sessionId, visitors, cartItems, hotelCart } = useAppSelector(
    (state) => state.cart,
  );
  const [activeTickets, setActiveTickets] = useState<any[]>([]);
  const [activeHotels, setActiveHotels] = useState<any[]>([]);
  const [RestrictDates, setRestrictDates] = useState<any[]>([]);

  const [selectedDate, setSelectedDate] = useState<string>("");
  const [highlightDateCard, setHighlightDateCard] = useState(false);

  const triggerDateHighlight = () => {
    setHighlightDateCard(true);
    setTimeout(() => {
      setHighlightDateCard(false);
    }, 3000);
  };

  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const loadTickets = async () => {
      if (isModalOpen && activeTickets.length === 0) {
        dispatch(setLoading(true));
        try {
          const ticketsData = await fetchAllActiveTickets();
          setActiveTickets(ticketsData);
        } catch (error) {
          console.error("Failed to fetch tickets", error);
          toast.error("Failed to load attractions. Please try again.");
        } finally {
          dispatch(setLoading(false));
        }
      }
    };
    loadTickets();
  }, [isModalOpen, activeTickets.length, dispatch]);
  const [isHotelModalOpen, setIsHotelModalOpen] = useState(false);
  const [localCartItems, setLocalCartItems] = useState<any[]>([]);

  const [availability, setAvailability] = useState<Record<number, any>>({});
  const [failedMinPaxCarts, setFailedMinPaxCarts] = useState<number[]>([]);
  const [failedSlotAttractions, setFailedSlotAttractions] = useState<string[]>(
    [],
  );
  const [failedHotelDates, setFailedHotelDates] = useState<number[]>([]);

  const [confirmDelete, setConfirmDelete] = useState<{
    isOpen: boolean;
    cartId: number | null;
    cartType: "Ticket" | "Hotel";
  }>({
    isOpen: false,
    cartId: null,
    cartType: "Ticket",
  });

  const initialized = useRef(false);
  const initialTicketProcessed = useRef<number | null>(null);
  const initialHotelProcessed = useRef<string | null>(null);
  const lastFetchedHotelsDate = useRef<string | null>(null);

  const formatDateForAPI = (dateStr: string) => {
    if (!dateStr) return "";
    if (dateStr.includes("-") && dateStr.split("-")[0].length === 4)
      return dateStr;
    if (dateStr.includes("/")) {
      const [d, m, y] = dateStr.split("/");
      return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return dateStr;
  };

  const processCartItems = (items: any[]) => {
    return items.map((item: any) => {
      const pax = item.TicketPaxDetails || [];
      const TotalAdults = Math.max(
        0,
        typeof item.TotalAdults === "number"
          ? item.TotalAdults
          : pax.filter((v: any) => v.Age > 15).length || 1,
      );
      const TotalChildSevenYr =
        item.TotalChildSevenYr ||
        pax.filter((v: any) => v.Age >= 7 && v.Age <= 15).length ||
        0;
      const TotalChildThreeYr =
        item.TotalChildThreeYr ||
        pax.filter((v: any) => v.Age >= 3 && v.Age < 7).length ||
        0;

      const serviceChargeType = item.ServiceChargeType || 0;
      const serviceCharge = item.ServiceCharge || 0;

      const baseTotal =
        TotalAdults * (item.PerPersonRate || 0) +
        TotalChildSevenYr * (item.ChildSevenYrRate || 0) +
        TotalChildThreeYr * (item.ChildThreeYrRate || 0);

      const totalTickets = TotalAdults + TotalChildSevenYr + TotalChildThreeYr;

      let serviceChargeAmount = 0;
      if (serviceChargeType === 1) {
        serviceChargeAmount = Number(serviceCharge) * totalTickets;
      } else if (serviceChargeType === 2) {
        serviceChargeAmount = (Number(baseTotal) * Number(serviceCharge)) / 100;
      } else {
        serviceChargeAmount = 0;
      }

      return {
        ...item,
        TotalAdults: TotalAdults,
        TotalChildSevenYr: TotalChildSevenYr,
        TotalChildThreeYr: TotalChildThreeYr,
        Total: baseTotal,
        BaseTotal: baseTotal,
        CalculatedServiceCharge: serviceChargeAmount,
      };
    });
  };

  useEffect(() => {
    const syncCart = async () => {
      if (!sessionId) return;

      if (initialized.current) return;
      initialized.current = true;

      dispatch(setLoading(true));
      try {
        let { TicketCart, HotelCart, SOUTicketSoldOutDateforpax, VisitDate } =
          await findByIDCart(sessionId);
        setRestrictDates(
          SOUTicketSoldOutDateforpax?.map((item: any) => item.SoldOutDate) ||
            [],
        );

        let currentVisitDate = "";
        if (VisitDate && VisitDate?.length > 0) {
          currentVisitDate = formatToYYYYMMDD(VisitDate[0]?.VisitDate);
          setSelectedDate(currentVisitDate);
        }

        if (
          initialTicketId &&
          initialTicketProcessed.current !== initialTicketId
        ) {
          const isAlreadyInCart = TicketCart.some(
            (item: any) => item.TicketID === initialTicketId,
          );
          if (!isAlreadyInCart) {
            const existingItem = TicketCart[0];
            const initialAdults = existingItem
              ? typeof existingItem.TotalAdults === "number"
                ? existingItem.TotalAdults
                : 1
              : visitors.filter((v: any) => v.Age > 15).length || 1;
            const initialChildSeven = existingItem
              ? existingItem.TotalChildSevenYr || 0
              : visitors.filter((v: any) => v.Age >= 7 && v.Age <= 15).length ||
                0;
            const initialChildThree = existingItem
              ? existingItem.TotalChildThreeYr || 0
              : visitors.filter((v: any) => v.Age >= 3 && v.Age < 7).length ||
                0;

            await addToCart({
              SessionID: sessionId,
              CartType: "Ticket",
              TicketID: initialTicketId,
              TicketPax: [],
              TotalAdults: initialAdults,
              TotalChildSevenYr: initialChildSeven,
              TotalChildThreeYr: initialChildThree,
              VisitDate: currentVisitDate
                ? formatDateForAPI(currentVisitDate)
                : null,
            });
            const updated = await findByIDCart(sessionId);
            TicketCart = updated.TicketCart;
            HotelCart = updated.HotelCart;
            setRestrictDates(
              updated.SOUTicketSoldOutDateforpax?.map(
                (item: any) => item.SoldOutDate,
              ) || [],
            );
          }
          initialTicketProcessed.current = initialTicketId;
        }

        const hotelKey = `${initialHotelId}_${initialRoomCategoryId}_${initialMealPlanID}`;
        if (initialHotelId && initialHotelProcessed.current !== hotelKey) {
          const isAlreadyInCart = HotelCart.some(
            (item: any) =>
              item.HotelID === initialHotelId &&
              item.RoomCategoryID === initialRoomCategoryId &&
              Number(item.MealPlanID || 0) === Number(initialMealPlanID || 0),
          );
          if (!isAlreadyInCart) {
            const today = new Date(new Date().getTime() + 360 * 60 * 1000)
              .toISOString()
              .split("T")[0];
            const checkIn = currentVisitDate
              ? formatDateForAPI(currentVisitDate)
              : today;
            const checkOut = new Date(
              new Date(checkIn).getTime() + 24 * 60 * 60 * 1000,
            )
              .toISOString()
              .split("T")[0];

            const adults = visitors.filter((v) => v.Age > 15).length;
            const children = visitors.filter((v) => v.Age <= 15).length;
            const maxPax = 2;

            await addToCart({
              SessionID: sessionId,
              CartType: "Hotel",
              HotelID: initialHotelId,
              RoomCategoryID: initialRoomCategoryId,
              MealPlanID: initialMealPlanID || 0,
              CheckIn: checkIn,
              CheckOut: checkOut,
              Night: 1,
              Rooms: Math.max(1, Math.floor((adults + children) / maxPax)),
              Adults: adults,
              Child: children,
              ExtraAdult:
                adults + children > maxPax ? (adults + children) % maxPax : 0,
            });
            const updated = await findByIDCart(sessionId);
            TicketCart = updated.TicketCart;
            HotelCart = updated.HotelCart;
            setRestrictDates(
              updated.SOUTicketSoldOutDateforpax?.map(
                (item: any) => item.SoldOutDate,
              ) || [],
            );
          }
          initialHotelProcessed.current = hotelKey;
        }

        const initialHotelUpdates: { [key: number]: any } = {};
        for (let hotel of HotelCart) {
          const currentTotal = (hotel.Adults || 0) + (hotel.Child || 0);
          if (currentTotal !== visitors.length && visitors.length > 0) {
            const adults = visitors.filter((v) => v.Age > 15).length;
            const children = visitors.filter((v) => v.Age <= 15).length;
            const totalPax = adults + children;
            const maxPax = hotel.TotalPaxAllowed || 2;

            const rooms = Math.max(1, Math.floor(totalPax / maxPax));
            const totalExtras = totalPax % maxPax;

            let extraAdult = 0;
            let extraChild = 0;
            if (totalExtras > 0) {
              if (children >= totalExtras) {
                extraChild = totalExtras;
              } else {
                extraChild = children;
                extraAdult = totalExtras - children;
              }
            }

            initialHotelUpdates[hotel.CartID] = {
              rooms: rooms,
              adults: adults,
              child: children,
              extraAdult: extraAdult,
              extraChild: extraChild,
              checkIn: hotel.CheckIn,
              checkOut: hotel.CheckOut,
              nights: hotel.Night,
              subtotal: hotel.TotalAmount || 0,
              gst: hotel.CalculatedGST || 0,
              total: hotel.FinalTotal || 0,
            };
          }
        }
        if (Object.keys(initialHotelUpdates).length > 0) {
          setHotelUpdates((prev) => ({ ...prev, ...initialHotelUpdates }));
        }

        const processedTicketCart = processCartItems(TicketCart);
        dispatch(setCartItems(processedTicketCart));
        dispatch(setHotelCart(HotelCart));
        setLocalCartItems(processedTicketCart);

        // Initialize availability from database saved slots instantly (with loading flag)
        const initialAvailability: Record<number, any> = {};
        for (const item of processedTicketCart) {
          const attractions = item.AttractionTicket || [];
          const availabilityData: Record<number, any> = {};

          for (const attr of attractions) {
            const slotId =
              attr.ID === 1
                ? item.TimeSlotID || item.ViewingGallerySlotID || null
                : item.ProjectionMappingID ||
                  item.ProjectionMappingSlotID ||
                  null;

            const selectedSlot = (attr.AttractionTicketChild || []).find(
              (s: any) => s.ID === slotId,
            );
            const isNotInterested =
              slotId === 0 ||
              (selectedSlot &&
                (selectedSlot.TimeSlot || "")
                  .toLowerCase()
                  .includes("not interested"));

            if (slotId) {
              availabilityData[attr.ID] = {
                id: isNotInterested ? 0 : slotId,
                seats: 0,
                loading:
                  slotId && slotId > 0 && !isNotInterested ? true : false,
              };
            }
          }
          initialAvailability[item.CartID] = availabilityData;
        }
        setAvailability((prev) => ({ ...prev, ...initialAvailability }));

        // Fetch seat availability asynchronously in the background (non-blocking)
        processedTicketCart.forEach((item: any) => {
          const attractions = item.AttractionTicket || [];
          const itemDate = formatToYYYYMMDD(item.VisitDate || item.Date);

          for (const attr of attractions) {
            const slotId =
              attr.ID === 1
                ? item.TimeSlotID || item.ViewingGallerySlotID || null
                : item.ProjectionMappingID ||
                  item.ProjectionMappingSlotID ||
                  null;

            const selectedSlot = (attr.AttractionTicketChild || []).find(
              (s: any) => s.ID === slotId,
            );
            const isNotInterested =
              slotId === 0 ||
              (selectedSlot &&
                (selectedSlot.TimeSlot || "")
                  .toLowerCase()
                  .includes("not interested"));

            if (slotId && slotId > 0 && !isNotInterested && itemDate) {
              findAvailableTicket({
                TimeSlotID: slotId,
                Date: itemDate,
              })
                .then((data) => {
                  const seats = data?.Available ?? 0;
                  setAvailability((prev) => {
                    const currentCart = prev[item.CartID] || {};
                    return {
                      ...prev,
                      [item.CartID]: {
                        ...currentCart,
                        [attr.ID]: {
                          id: slotId,
                          seats,
                          loading: false,
                        },
                      },
                    };
                  });
                })
                .catch((err) => {
                  console.error(
                    `Failed to fetch seats for slot ${slotId}:`,
                    err,
                  );
                  setAvailability((prev) => {
                    const currentCart = prev[item.CartID] || {};
                    return {
                      ...prev,
                      [item.CartID]: {
                        ...currentCart,
                        [attr.ID]: {
                          id: slotId,
                          seats: 0,
                          loading: false,
                        },
                      },
                    };
                  });
                });
            }
          }
        });

        initialized.current = true;
        if (initialTicketId || initialHotelId) {
          router.replace("/cart");
        }
      } catch (error) {
        console.error("Failed to sync cart", error);
      } finally {
        dispatch(setLoading(false));
      }
    };

    syncCart();
  }, [
    sessionId,
    initialTicketId,
    initialHotelId,
    initialRoomCategoryId,
    initialMealPlanID,
    dispatch,
  ]);

  useEffect(() => {
    setLocalCartItems(cartItems);
  }, [cartItems]);

  const handleCountChange = async (
    cartId: number,
    type: "Adult" | "Child7-15" | "Child3-6",
    delta: number,
  ) => {
    const clickedItem = localCartItems.find((i) => i.CartID === cartId);
    if (!clickedItem) return;

    let newAdults = clickedItem.TotalAdults || 0;
    let newChild7 = clickedItem.TotalChildSevenYr || 0;
    let newChild3 = clickedItem.TotalChildThreeYr || 0;

    if (type === "Adult") newAdults = Math.max(0, newAdults + delta);
    if (type === "Child7-15") newChild7 = Math.max(0, newChild7 + delta);
    if (type === "Child3-6") newChild3 = Math.max(0, newChild3 + delta);

    if (delta > 0) {
      if (type === "Adult" && newAdults > MAX_ADULT_TICKETS) {
        toast.error(`Maximum ${MAX_ADULT_TICKETS} adult tickets can be booked`, {
          icon: "⚠️",
          id: `toast-limit-${cartId}`,
        });
        return;
      }
      if (type === "Child7-15" && newChild7 > MAX_CHILD_TICKETS) {
        toast.error(`Maximum ${MAX_CHILD_TICKETS} child tickets can be booked`, {
          icon: "⚠️",
          id: `toast-limit-${cartId}`,
        });
        return;
      }
    }

    const nextCounts = {
      TotalAdults: newAdults,
      TotalChildSevenYr: newChild7,
      TotalChildThreeYr: newChild3,
    };

    const nextTotalTickets = newAdults + newChild7 + newChild3;

    // Since guest counts are synchronized across all tickets in the cart, check and clear
    // the highlight of ANY ticket that now meets its MinPaxCount requirement.
    setFailedMinPaxCarts((prev) => {
      if (prev.length === 0) return prev;
      return prev.filter((failedCartId) => {
        const item = localCartItems.find((i) => i.CartID === failedCartId);
        if (!item) return false;
        const minPax = Number(item.MinPaxCount) || 0;
        return nextTotalTickets < minPax;
      });
    });

    for (const i of localCartItems) {
      const attractions = i.AttractionTicket || [];

      let minAvailable = 50;
      for (const attr of attractions) {
        const slotId = availability[i.CartID]?.[attr.ID]?.id;
        const avail =
          slotId &&
          slotId > 0 &&
          availability[i.CartID]?.[attr.ID]?.seats !== undefined
            ? availability[i.CartID][attr.ID].seats
            : 50;

        if (attr.ID === 1) {
          minAvailable = Math.min(minAvailable, Math.max(0, avail));
        } else if (slotId && slotId > 0) {
          minAvailable = Math.min(minAvailable, Math.max(0, avail));
        }
      }

      if (delta > 0 && nextTotalTickets > minAvailable) {
        if (minAvailable < 50) {
          if (minAvailable > 0) {
            toast.error(
              `Only ${minAvailable} tickets available in selected slot(s) for ${i.TicketName}`,
              {
                icon: "⚠️",
                id: `toast-limit-${i.CartID}`,
              },
            );
          } else {
            toast.error(
              `No tickets available in selected slot(s) for ${i.TicketName}`,
              {
                icon: "⚠️",
                id: `toast-limit-${i.CartID}`,
              },
            );
          }
        } else {
          toast.error(`Maximum 50 tickets can be booked per attraction`, {
            icon: "⚠️",
            id: `toast-limit-${i.CartID}`,
          });
        }
        return;
      }
    }

    const updatedItems = localCartItems.map((i) => {
      const baseTotal =
        nextCounts.TotalAdults * (i.PerPersonRate || 0) +
        nextCounts.TotalChildSevenYr * (i.ChildSevenYrRate || 0) +
        nextCounts.TotalChildThreeYr * (i.ChildThreeYrRate || 0);

      const totalTickets =
        nextCounts.TotalAdults +
        nextCounts.TotalChildSevenYr +
        nextCounts.TotalChildThreeYr;

      const serviceChargeType = i.ServiceChargeType || 0;
      const serviceCharge = i.ServiceCharge || 0;

      let serviceChargeAmount = 0;
      if (serviceChargeType === 1) {
        serviceChargeAmount = Number(serviceCharge) * totalTickets;
      } else if (serviceChargeType === 2) {
        serviceChargeAmount = (Number(baseTotal) * Number(serviceCharge)) / 100;
      } else {
        serviceChargeAmount = 0;
      }

      return {
        ...i,
        ...nextCounts,
        Total: baseTotal,
        BaseTotal: baseTotal,
        CalculatedServiceCharge: serviceChargeAmount,
      };
    });

    setLocalCartItems(updatedItems);
    dispatch(setCartItems(updatedItems));

    if (!sessionId) return;

    try {
      await Promise.all(
        updatedItems.map((i) => {
          const currentViewingSlot = availability[i.CartID]?.[1]?.id || null;
          const currentProjectionSlot = availability[i.CartID]?.[2]?.id || null;

          return updateTicketQuantity({
            SessionID: sessionId,
            TotalAdults: nextCounts.TotalAdults,
            TotalChildSevenYr: nextCounts.TotalChildSevenYr,
            TotalChildThreeYr: nextCounts.TotalChildThreeYr,
            ProjectionMappingID: currentProjectionSlot,
            TimeSlotID: currentViewingSlot,
            VisitDate:
              formatDateForAPI(selectedDate || i.VisitDate || i.Date) || null,
          });
        }),
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to sync guest counts with server");
    }
  };

  const handleSlotChange = async (
    cartId: number,
    slotId: number | null,
    attractionId: number,
  ) => {
    if (!selectedDate) {
      toast.error("Please select a date first", { icon: "📅" });
      triggerDateHighlight();
      const header = document.querySelector(".header-date-selector");
      header?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (!sessionId) return;
    const item = localCartItems.find((i) => i.CartID === cartId);
    if (!item) return;

    if (slotId && slotId > 0) {
      const failedKey = `${cartId}_${attractionId}`;
      setFailedSlotAttractions((prev) =>
        prev.filter((key) => key !== failedKey),
      );
    }

    const currentViewingSlot =
      attractionId === 1 ? slotId : availability[cartId]?.[1]?.id || null;
    const currentProjectionSlot =
      attractionId === 2 ? slotId : availability[cartId]?.[2]?.id || null;

    // 1. Instantly update availability slot IDs in local state so the dropdown updates immediately without delay
    setAvailability((prev) => ({
      ...prev,
      [cartId]: {
        ...(prev[cartId] || {}),
        [attractionId]: {
          seats: prev[cartId]?.[attractionId]?.seats ?? 0,
          id: slotId,
          loading: slotId && slotId > 0 ? true : false,
        },
      },
    }));

    // Instantly update items in local state & Redux so state matches selections immediately
    let updatedItems = localCartItems.map((i) => {
      if (i.CartID !== cartId) return i;
      return {
        ...i,
        ViewingGallerySlotID: currentViewingSlot || 0,
        ProjectionMappingSlotID: currentProjectionSlot || 0,
        TimeSlotID: currentViewingSlot || 0,
        ProjectionMappingID: currentProjectionSlot || 0,
      };
    });
    setLocalCartItems(updatedItems);
    dispatch(setCartItems(updatedItems));

    // 2. Fetch available tickets from the API in the background
    let seats = 0;
    if (slotId && slotId > 0) {
      try {
        const data = await findAvailableTicket({
          TimeSlotID: slotId,
          Date: selectedDate,
        });
        seats = data?.Available ?? 0;
      } catch (error) {
        console.error("Failed to check availability:", error);
        toast.error("Tickets not available for this slot", { icon: "🚫" });
        seats = 0;
      }
    }

    // 3. Update the seats count in state
    setAvailability((prev) => ({
      ...prev,
      [cartId]: {
        ...(prev[cartId] || {}),
        [attractionId]: { seats, id: slotId, loading: false },
      },
    }));

    // Calculate maximum available limit based on current and newly checked slot seats
    const vgAvail =
      attractionId === 1
        ? seats
        : currentViewingSlot &&
            currentViewingSlot > 0 &&
            availability[cartId]?.[1]?.seats !== undefined
          ? availability[cartId][1].seats
          : 50;

    const pmAvail =
      attractionId === 2
        ? seats
        : currentProjectionSlot &&
            currentProjectionSlot > 0 &&
            availability[cartId]?.[2]?.seats !== undefined
          ? availability[cartId][2].seats
          : 50;

    const minAvailable = Math.min(
      50,
      Math.max(0, vgAvail),
      currentProjectionSlot && currentProjectionSlot > 0
        ? Math.max(0, pmAvail)
        : 50,
    );

    const currentTotal =
      (item.TotalAdults || 0) +
      (item.TotalChildSevenYr || 0) +
      (item.TotalChildThreeYr || 0);

    let nextAdults = item.TotalAdults || 0;
    let nextChild7 = item.TotalChildSevenYr || 0;
    let nextChild3 = item.TotalChildThreeYr || 0;

    try {
      await updateTicketQuantity({
        SessionID: sessionId,
        TicketID: item.TicketID,
        TotalAdults: nextAdults,
        TotalChildSevenYr: nextChild7,
        TotalChildThreeYr: nextChild3,
        ProjectionMappingID: currentProjectionSlot,
        TimeSlotID: currentViewingSlot,
        VisitDate:
          formatDateForAPI(selectedDate || item.VisitDate || item.Date) || null,
      });

      // Keep local state and Redux store in sync
      updatedItems = localCartItems.map((i) => {
        if (i.CartID !== cartId) return i;
        const baseTotal =
          nextAdults * (i.PerPersonRate || 0) +
          nextChild7 * (i.ChildSevenYrRate || 0) +
          nextChild3 * (i.ChildThreeYrRate || 0);

        const totalTickets = nextAdults + nextChild7 + nextChild3;
        const serviceChargeType = i.ServiceChargeType || 0;
        const serviceCharge = i.ServiceCharge || 0;

        let serviceChargeAmount = 0;
        if (serviceChargeType === 1) {
          serviceChargeAmount = Number(serviceCharge) * totalTickets;
        } else if (serviceChargeType === 2) {
          serviceChargeAmount =
            (Number(baseTotal) * Number(serviceCharge)) / 100;
        }

        return {
          ...i,
          TotalAdults: nextAdults,
          TotalChildSevenYr: nextChild7,
          TotalChildThreeYr: nextChild3,
          Total: baseTotal,
          BaseTotal: baseTotal,
          CalculatedServiceCharge: serviceChargeAmount,
          ViewingGallerySlotID: currentViewingSlot || 0,
          ProjectionMappingSlotID: currentProjectionSlot || 0,
          TimeSlotID: currentViewingSlot || 0,
          ProjectionMappingID: currentProjectionSlot || 0,
        };
      });
      setLocalCartItems(updatedItems);
      dispatch(setCartItems(updatedItems));
    } catch (error: any) {
      toast.error(error?.message || "Failed to update ticket slot");
    }
  };

  const handleRemoveFromCart = async () => {
    const { cartId, cartType } = confirmDelete;
    if (!cartId) return;

    dispatch(setLoading(true));
    try {
      await deleteCart(cartId, cartType);
      const { TicketCart, HotelCart, SOUTicketSoldOutDateforpax } =
        await findByIDCart(sessionId!);
      const processed = processCartItems(TicketCart);
      dispatch(setCartItems(processed));
      dispatch(setHotelCart(HotelCart));
      setLocalCartItems(processed);
      setRestrictDates(
        SOUTicketSoldOutDateforpax?.map((item: any) => item.SoldOutDate) || [],
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to remove item");
    } finally {
      dispatch(setLoading(false));
      setConfirmDelete({ isOpen: false, cartId: null, cartType: "Ticket" });
    }
  };

  const [hotelUpdates, setHotelUpdates] = useState<{ [key: number]: any }>({});

  const hasUnavailableHotelRooms = useMemo(() => {
    if (!hotelCart || hotelCart.length === 0) return false;
    return hotelCart.some((hotel) => {
      const update = hotelUpdates[hotel.CartID];
      const maxAvailable =
        update !== undefined
          ? update.availableRooms
          : hotel.AvailableRooms !== undefined
            ? hotel.AvailableRooms
            : 5;
      const roomsCount = update !== undefined ? update.rooms : hotel.Rooms || 1;
      return maxAvailable <= 0 || roomsCount > maxAvailable;
    });
  }, [hotelCart, hotelUpdates]);

  const handleHotelUpdate = useCallback(
    async (id: number, data: any) => {
      if (data.checkIn && data.checkOut) {
        setFailedHotelDates((prev) => prev.filter((cartId) => cartId !== id));
      }
      setHotelUpdates((prev) => {
        const current = prev[id];
        if (JSON.stringify(current) === JSON.stringify(data)) {
          return prev;
        }
        return { ...prev, [id]: data };
      });

      if (!sessionId) return;
      const hotel = hotelCart.find((h) => h.CartID === id);
      if (!hotel) return;

      // Check if values actually changed to avoid redundant API calls
      const isChanged =
        formatDateForAPI(data.checkIn) !== formatDateForAPI(hotel.CheckIn) ||
        formatDateForAPI(data.checkOut) !== formatDateForAPI(hotel.CheckOut) ||
        data.nights !== hotel.Night ||
        data.rooms !== hotel.Rooms ||
        data.adults !== hotel.Adults ||
        data.child !== hotel.Child ||
        data.extraAdult !== hotel.ExtraAdult;

      if (!isChanged) return;

      try {
        await updateHotelQuantity({
          SessionID: sessionId,
          HotelID: hotel.HotelID,
          MealPlanID: hotel.MealPlanID || 0,
          RoomCategoryID: hotel.RoomCategoryID,
          CheckIn: data.checkIn ? formatDateForAPI(data.checkIn) : null,
          CheckOut: data.checkOut ? formatDateForAPI(data.checkOut) : null,
          Night: data.nights,
          Rooms: data.rooms,
          Adults: data.adults,
          Child: data.child,
          ExtraAdult: data.extraAdult,
        });
      } catch (err: any) {
        console.error("Failed to update hotel quantity:", err);
      }
    },
    [sessionId, hotelCart],
  );

  const calculateSubtotal = () => {
    let total = 0;
    if (Array.isArray(localCartItems)) {
      total += localCartItems.reduce(
        (acc, item) =>
          acc + (item.Total || 0) + (item.CalculatedServiceCharge || 0),
        0,
      );
    }
    if (Array.isArray(hotelCart)) {
      total += hotelCart.reduce((acc, item) => {
        const liveUpdate = hotelUpdates[item.CartID];
        return (
          acc +
          (liveUpdate !== undefined ? liveUpdate.total : item.TotalAmount || 0)
        );
      }, 0);
    }
    return total;
  };

  const handleAddFromModal = async (ticket: any) => {
    if (!sessionId) {
      toast.error("Session not initialized");
      return;
    }
    dispatch(setLoading(true));
    try {
      const existingItem = localCartItems[0];
      const initialAdults = existingItem
        ? typeof existingItem.TotalAdults === "number"
          ? existingItem.TotalAdults
          : 1
        : visitors.filter((v: any) => v.Age > 15).length || 1;
      const initialChildSeven = existingItem
        ? existingItem.TotalChildSevenYr || 0
        : 0;
      const initialChildThree = existingItem
        ? existingItem.TotalChildThreeYr || 0
        : 0;

      await addToCart({
        SessionID: sessionId,
        CartType: "Ticket",
        TicketID: ticket.ID,
        TicketPax: [],
        TotalAdults: initialAdults,
        TotalChildSevenYr: initialChildSeven,
        TotalChildThreeYr: initialChildThree,
        VisitDate: formatDateForAPI(selectedDate) || null,
      });
      const { TicketCart, HotelCart, SOUTicketSoldOutDateforpax } =
        await findByIDCart(sessionId);
      const processed = processCartItems(TicketCart);
      dispatch(setCartItems(processed));
      dispatch(setHotelCart(HotelCart));
      setLocalCartItems(processed);
      setRestrictDates(
        SOUTicketSoldOutDateforpax?.map((item: any) => item.SoldOutDate) || [],
      );
      setIsModalOpen(false);
    } catch (error: any) {
      toast.error(error?.message || "Failed to add ticket");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleAddHotelFromModal = async (
    hotelId: number,
    roomCategoryId: number,
    mealPlanId: number,
  ) => {
    if (!sessionId) {
      toast.error("Session not initialized");
      return;
    }
    dispatch(setLoading(true));
    try {
      const today = new Date(new Date().getTime() + 360 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      const checkIn = selectedDate ? formatDateForAPI(selectedDate) : today;
      const checkOut = new Date(
        new Date(checkIn).getTime() + 24 * 60 * 60 * 1000,
      )
        .toISOString()
        .split("T")[0];

      const adults = visitors.filter((v) => v.Age > 15).length || 1;
      const children = visitors.filter((v) => v.Age <= 15).length;

      await addToCart({
        SessionID: sessionId,
        CartType: "Hotel",
        HotelID: hotelId,
        RoomCategoryID: roomCategoryId,
        MealPlanID: mealPlanId || 0,
        CheckIn: checkIn,
        CheckOut: checkOut,
        Night: 1,
        Rooms: 1,
        Adults: adults,
        Child: children,
        ExtraAdult: 0,
      });
      const { TicketCart, HotelCart, SOUTicketSoldOutDateforpax } =
        await findByIDCart(sessionId);
      const processed = processCartItems(TicketCart);
      dispatch(setCartItems(processed));
      dispatch(setHotelCart(HotelCart));
      setLocalCartItems(processed);
      setRestrictDates(
        SOUTicketSoldOutDateforpax?.map((item: any) => item.SoldOutDate) || [],
      );
      setIsHotelModalOpen(false);
      toast.success("Hotel added to cart!");
    } catch (error: any) {
      toast.error(error?.message || "Failed to add hotel");
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleFinalContinue = async () => {
    if (calculateSubtotal() <= 0) {
      toast.error("Minimum 1 quantity required to proceed.", { icon: "🚫" });
      return;
    }

    if (localCartItems.length > 0 && !selectedDate) {
      toast.error("Please select a visit date first", { icon: "📅" });
      triggerDateHighlight();
      const header = document.querySelector(".header-date-selector");
      header?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const newFailedCarts: number[] = [];
    for (const item of localCartItems) {
      const itemTotal =
        (item.TotalAdults || 0) +
        (item.TotalChildSevenYr || 0) +
        (item.TotalChildThreeYr || 0);

      const minPax = Number(item.MinPaxCount) || 0;
      if (minPax > 0 && itemTotal < minPax) {
        newFailedCarts.push(item.CartID);
      }
    }

    if (newFailedCarts.length > 0) {
      setFailedMinPaxCarts(newFailedCarts);
      const firstFailedItem = localCartItems.find((item) =>
        newFailedCarts.includes(item.CartID),
      );
      if (firstFailedItem) {
        // const itemTotal =
        //   (firstFailedItem.TotalAdults || 0) +
        //   (firstFailedItem.TotalChildSevenYr || 0) +
        //   (firstFailedItem.TotalChildThreeYr || 0);
        // const minPax = Number(firstFailedItem.MinPaxCount) || 0;
        // toast.error(
        //   `Minimum ${minPax} tickets are required for ${firstFailedItem.TicketName}. You have selected ${itemTotal} ticket(s).`,
        //   { icon: "🚫" },
        // );
        setTimeout(() => {
          const element = document.getElementById(
            `cart-item-${firstFailedItem.CartID}`,
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
      }
      return;
    }

    setFailedMinPaxCarts([]);

    for (const item of localCartItems) {
      const itemTotal =
        (item.TotalAdults || 0) +
        (item.TotalChildSevenYr || 0) +
        (item.TotalChildThreeYr || 0);

      const attractions = item.AttractionTicket || [];

      let minAvailable = 50;
      for (const attr of attractions) {
        const slotId = availability[item.CartID]?.[attr.ID]?.id;
        const avail =
          slotId &&
          slotId > 0 &&
          availability[item.CartID]?.[attr.ID]?.seats !== undefined
            ? availability[item.CartID][attr.ID].seats
            : 50;

        const hasServerNotInterested = (attr.AttractionTicketChild || []).some(
          (slot: any) =>
            (slot.TimeSlot || "").toLowerCase().includes("not interested"),
        );
        // Slot is required unless the attraction offers a "Not Interested" option
        const isRequired = !hasServerNotInterested;

        if (isRequired) {
          if (!slotId || slotId <= 0) {
            toast.error(`Please select a time slot for ${attr.Title || item.TicketName}`, {
              icon: "⏰",
            });
            const failedKey = `${item.CartID}_${attr.ID}`;
            setFailedSlotAttractions((prev) =>
              prev.includes(failedKey) ? prev : [...prev, failedKey],
            );
            setTimeout(() => {
              const element = document.getElementById(
                `slot-select-${item.CartID}-${attr.ID}`,
              );
              if (element) {
                element.scrollIntoView({ behavior: "smooth", block: "center" });
                element.focus();
              }
            }, 100);
            return;
          }
          if (avail <= 0) {
            toast.error(
              `The selected slot for ${item.TicketName} is sold out. Please choose another time.`,
              { icon: "🚫" },
            );
            return;
          }
          if (itemTotal > avail) {
            toast.error(
              `Only ${avail} tickets available for the selected slot of ${item.TicketName}. You have selected ${itemTotal} tickets.`,
              { icon: "🚫" },
            );
            return;
          }
          minAvailable = Math.min(minAvailable, Math.max(0, avail));
        } else {
          const selectedSlot = (attr.AttractionTicketChild || []).find(
            (s: any) => s.ID === slotId,
          );
          const isNotInterestedSelected =
            slotId === 0 ||
            (selectedSlot &&
              (selectedSlot.TimeSlot || "")
                .toLowerCase()
                .includes("not interested"));

          if (slotId && slotId > 0 && !isNotInterestedSelected) {
            if (avail <= 0) {
              toast.error(`The selected slot for ${attr.Title} is sold out.`, {
                icon: "🚫",
              });
              return;
            }
            if (itemTotal > avail) {
              toast.error(
                `Only ${avail} tickets available for the selected slot of ${attr.Title}. You have selected ${itemTotal} tickets.`,
                { icon: "🚫" },
              );
              return;
            }
            minAvailable = Math.min(minAvailable, Math.max(0, avail));
          }
        }
      }

      if (itemTotal > minAvailable) {
        toast.error(
          `Only ${minAvailable} tickets available across selected slots for ${item.TicketName}.`,
          { icon: "🚫" },
        );
        return;
      }
    }

    for (const hotel of hotelCart) {
      const update = hotelUpdates[hotel.CartID];
      const checkIn = update !== undefined ? update.checkIn : hotel.CheckIn;
      const checkOut = update !== undefined ? update.checkOut : hotel.CheckOut;

      if (!checkIn || !checkOut) {
        toast.error(
          `Please select check-in and check-out dates for ${hotel.HotelName}`,
          { icon: "📅" },
        );
        setFailedHotelDates((prev) =>
          prev.includes(hotel.CartID) ? prev : [...prev, hotel.CartID],
        );
        setTimeout(() => {
          const element = document.getElementById(`hotel-card-${hotel.CartID}`);
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);
        return;
      }

      const currentRooms =
        update !== undefined ? update.rooms : hotel.Rooms || 0;
      if (currentRooms <= 0) {
        toast.error(`Please select at least 1 room for ${hotel.HotelName}`, {
          icon: "🏨",
        });
        return;
      }
    }

    dispatch(setLoading(true));
    try {
      if (sessionId) {
        for (const hotel of hotelCart) {
          const update = hotelUpdates[hotel.CartID];
          if (update) {
            await updateHotelQuantity({
              SessionID: sessionId,
              HotelID: hotel.HotelID,
              MealPlanID: hotel.MealPlanID || 0,
              RoomCategoryID: hotel.RoomCategoryID,
              CheckIn: update.checkIn ? formatDateForAPI(update.checkIn) : null,
              CheckOut: update.checkOut
                ? formatDateForAPI(update.checkOut)
                : null,
              Night: update.nights,
              Rooms: update.rooms,
              Adults: update.adults,
              Child: update.child,
              ExtraAdult: update.extraAdult,
            });
          }
        }
      }

      const finalItems = localCartItems.map((item) => {
        const attractions = item.AttractionTicket || [];
        const selectedSlotIds: number[] = [];
        for (const attr of attractions) {
          const slotId = availability[item.CartID]?.[attr.ID]?.id;
          if (slotId && slotId > 0) {
            selectedSlotIds.push(slotId);
          }
        }
        const slotIDsString = selectedSlotIds.join(",");

        return {
          ...item,
          TicketPaxDetails: [],
          TotalAdults: item.TotalAdults || 0,
          TotalChildSevenYr: item.TotalChildSevenYr || 0,
          TotalChildThreeYr: item.TotalChildThreeYr || 0,
          ViewingGallerySlotID: availability[item.CartID]?.[1]?.id || 0,
          ProjectionMappingSlotID: availability[item.CartID]?.[2]?.id || 0,
          TimeSlotID: availability[item.CartID]?.[1]?.id || 0,
          ProjectionMappingID: availability[item.CartID]?.[2]?.id || 0,
          SlotIDs: slotIDsString,
        };
      });

      const updatedHotelCart = hotelCart.map((hotel) => {
        const update = hotelUpdates[hotel.CartID];
        if (!update) return hotel;
        return {
          ...hotel,
          TotalAmount: update.subtotal,
          CalculatedGST: update.gst,
          FinalTotal: update.total,
          Rooms: update.rooms,
          Adults: update.adults,
          ExtraAdult: update.extraAdult,
          Child: update.child,
          ExtraChild: update.extraChild,
          CheckIn: update.checkIn,
          CheckOut: update.checkOut,
          Night: update.nights,
          AvailableRooms:
            update.availableRooms !== undefined
              ? update.availableRooms
              : hotel.AvailableRooms,
        };
      });

      dispatch(setCartItems(finalItems));
      dispatch(setHotelCart(updatedHotelCart));
      dispatch(setTicketDate(selectedDate));
      dispatch(setStep(1));
      window.scrollTo(0, 0);
    } catch (error: any) {
      toast.error(error?.message || "Failed to update hotel details");
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div className="space-y-6 sm:space-y-8 animate-in fade-in duration-500">
      {/* Date & Attraction Header */}
      {(localCartItems.length > 0 || hotelCart?.length === 0) && (
        <>
          <div
            className={`header-date-selector flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 sm:p-6 rounded-2xl sm:rounded-3xl border transition-all duration-300 ${
              highlightDateCard
                ? "border-red-500 ring-4 ring-red-100/80 shadow-[0_0_15px_rgba(239,68,68,0.15)] animate-shake !bg-red-50/50"
                : "border-orange-50 shadow-sm"
            }`}
          >
            <div className="space-y-0.5">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">
                Your <span className="text-orange-600">Attractions</span>
              </h2>
              <p className="text-gray-400 text-xs font-medium italic">
                Review and customize your experience
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4">
              <AttractionDatePicker
                selectedDate={selectedDate}
                disabledDates={RestrictDates}
                onDateSelect={async (date) => {
                  setSelectedDate(date);
                  setAvailability({});

                  if (!sessionId) return;
                  try {
                    if (localCartItems.length > 0) {
                      for (const i of localCartItems) {
                        await updateTicketQuantity({
                          SessionID: sessionId,
                          TotalAdults: i.TotalAdults || 0,
                          TotalChildSevenYr: i.TotalChildSevenYr || 0,
                          TotalChildThreeYr: i.TotalChildThreeYr || 0,
                          ProjectionMappingID: null,
                          TimeSlotID: null,
                          VisitDate: formatDateForAPI(date) || null,
                        });
                      }
                    } else {
                      await updateTicketQuantity({
                        SessionID: sessionId,
                        TotalAdults: 0,
                        TotalChildSevenYr: 0,
                        TotalChildThreeYr: 0,
                        ProjectionMappingID: null,
                        TimeSlotID: null,
                        VisitDate: formatDateForAPI(date) || null,
                      });
                    }
                    const { TicketCart, HotelCart } =
                      await findByIDCart(sessionId);
                    const processed = processCartItems(TicketCart);
                    dispatch(setCartItems(processed));
                    dispatch(setHotelCart(HotelCart));
                    setLocalCartItems(processed);
                  } catch (error: any) {
                    toast.error(
                      error?.message || "Failed to update visit date",
                    );
                  }
                }}
              />
            </div>
          </div>

          <div className="space-y-4 sm:space-y-2">
            {Array.isArray(localCartItems) && localCartItems.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:gap-6">
                <AnimatePresence mode="popLayout">
                  {localCartItems.map((item: any, idx: number) => (
                    <motion.div
                      key={item.CartID || idx}
                      id={`cart-item-${item.CartID}`}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.98 }}
                      className={`rounded-2xl sm:rounded-3xl shadow-sm p-4 sm:p-5 space-y-4 sm:space-y-5 relative overflow-visible transition-all duration-300 ${
                        failedMinPaxCarts.includes(item.CartID)
                          ? "bg-red-50/50 border-2 border-red-500 shadow-md ring-4 ring-red-500/10"
                          : "bg-[#FFF9F6] border border-orange-50"
                      }`}
                    >
                      {failedMinPaxCarts.includes(item.CartID) && (
                        <div className="flex items-start gap-2.5 p-3.5 bg-red-50 border border-red-200 rounded-2xl text-red-800 text-xs sm:text-sm font-semibold shadow-sm animate-in fade-in slide-in-from-top-2 duration-300">
                          <ShieldAlert className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 shrink-0 mt-0.5" />
                          <div className="space-y-0.5">
                            <p className="font-bold text-red-900">
                              Minimum Tickets Required
                            </p>
                            <p className="font-medium text-red-700">
                              Minimum {item.MinPaxCount} tickets are required
                              for {item.TicketName}. You have selected only{" "}
                              {(item.TotalAdults || 0) +
                                (item.TotalChildSevenYr || 0) +
                                (item.TotalChildThreeYr || 0)}{" "}
                              ticket(s). Please add more tickets below to
                              proceed.
                            </p>
                          </div>
                        </div>
                      )}

                      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 md:gap-6 items-start relative">
                        <button
                          onClick={() =>
                            setConfirmDelete({
                              isOpen: true,
                              cartId: item.CartID,
                              cartType: "Ticket",
                            })
                          }
                          className="absolute top-2 sm:top-0 right-2 sm:right-0 z-20 flex flex-col items-center gap-1 text-orange-400 hover:text-orange-600 transition-colors cursor-pointer group"
                        >
                          <div className="w-8 h-8 rounded-full bg-white text-orange-600 flex items-center justify-center shadow-md border border-orange-50 group-hover:bg-orange-500 group-hover:text-white transition-all">
                            <MdDelete className="w-4 h-4" />
                          </div>
                        </button>
                        <div className="relative w-full h-44 sm:w-28 sm:h-28 md:w-44 md:h-28 rounded-md overflow-hidden shadow-sm shrink-0">
                          <Image
                            src={item.TicketImage || "/placeholder.jpg"}
                            alt={item.TicketName}
                            fill
                            className="object-cover"
                          />
                        </div>
                        <div className="flex-1 space-y-3 min-w-0 w-full">
                          <div className="space-y-1">
                            <h3 className="text-sm sm:text-base md:text-lg font-semibold text-gray-900 pr-8 sm:pr-12 md:pr-16 leading-tight break-words">
                              {item.TicketName}
                            </h3>
                            {item.ShortDescription && (
                              <p className="text-[11px] sm:text-xs text-gray-500 italic max-w-2xl leading-normal font-medium line-clamp-2 md:line-clamp-none">
                                "{item.ShortDescription}"
                              </p>
                            )}
                            {(item.Notice ||
                              activeTickets.find((t) => t.ID === item.TicketID)
                                ?.Notice) && (
                              <div className="inline-flex items-center gap-1.5 px-2.5 py-0.5 sm:px-3 sm:py-1 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-[10px] sm:text-xs font-medium shadow-xs mt-1">
                                <Info className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-amber-600 shrink-0" />
                                <span className="break-words">
                                  {item.Notice ||
                                    activeTickets.find(
                                      (t) => t.ID === item.TicketID,
                                    )?.Notice}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Dynamic Attraction Dropdowns */}
                      {(() => {
                        const attractions = item.AttractionTicket || [];

                        if (attractions.length === 0) return null;

                        return (
                          <div className="border-t border-orange-100 border-dashed pt-4 mt-2 space-y-4">
                            {attractions.map((attr: any, aIdx: number) => {
                              return (
                                <div
                                  key={attr.ID || aIdx}
                                  className="space-y-3"
                                >
                                  {/* Dropdown Box */}
                                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 py-3 px-3 sm:px-4 bg-white rounded-2xl border border-gray-100">
                                    <span className="text-[13px] font-bold text-gray-700 whitespace-nowrap min-w-[120px]">
                                      {attr.Title}
                                    </span>

                                    <div className="flex flex-wrap items-center gap-4 flex-1 w-full sm:w-auto">
                                      <div className="relative group w-full sm:w-auto">
                                        <div className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none">
                                          <Clock className="w-3.5 h-3.5 text-orange-500" />
                                        </div>
                                        <select
                                          value={(() => {
                                            const currentId =
                                              availability[item.CartID]?.[
                                                attr.ID
                                              ]?.id;
                                            if (
                                              currentId === undefined ||
                                              currentId === null
                                            )
                                              return "";

                                            const selectedSlot = (
                                              attr.AttractionTicketChild || []
                                            ).find(
                                              (s: any) => s.ID === currentId,
                                            );
                                            const isNotInterested =
                                              currentId === 0 ||
                                              (selectedSlot &&
                                                (selectedSlot.TimeSlot || "")
                                                  .toLowerCase()
                                                  .includes("not interested"));

                                            return isNotInterested
                                              ? "0"
                                              : String(currentId);
                                          })()}
                                          onChange={(e) => {
                                            const val = e.target.value;
                                            handleSlotChange(
                                              item.CartID,
                                              val ? parseInt(val) : null,
                                              attr.ID,
                                            );
                                          }}
                                          id={`slot-select-${item.CartID}-${attr.ID}`}
                                          className={`appearance-none pl-9 pr-8 py-1.5 rounded-2xl border bg-white text-[12px] font-bold text-slate-900 outline-none cursor-pointer focus:ring-2 transition-all w-full sm:w-auto min-w-[150px] shadow-sm ${
                                            failedSlotAttractions.includes(
                                              `${item.CartID}_${attr.ID}`,
                                            )
                                              ? "border-red-500 ring-4 ring-red-100/80 animate-shake"
                                              : "border-orange-500 focus:ring-orange-100"
                                          }`}
                                        >
                                          <option value="">Select Slot</option>
                                          {(
                                            attr.AttractionTicketChild || []
                                          ).map((slot: any) => {
                                            const isNotInterested = (
                                              slot.TimeSlot || ""
                                            )
                                              .toLowerCase()
                                              .includes("not interested");
                                            return (
                                              <option
                                                key={slot.ID}
                                                value={
                                                  isNotInterested
                                                    ? "0"
                                                    : slot.ID
                                                }
                                              >
                                                {slot.TimeSlot}
                                              </option>
                                            );
                                          })}
                                        </select>
                                        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-gray-300">
                                          <ChevronRight className="w-3.5 h-3.5 rotate-90" />
                                        </div>
                                      </div>

                                      {/* Availability Display */}
                                      {(() => {
                                        const slotState =
                                          availability[item.CartID]?.[attr.ID];
                                        if (
                                          !slotState ||
                                          !slotState.id ||
                                          slotState.id === 0
                                        )
                                          return null;

                                        const selectedSlot = (
                                          attr.AttractionTicketChild || []
                                        ).find(
                                          (s: any) => s.ID === slotState.id,
                                        );
                                        const isNotInterested =
                                          selectedSlot &&
                                          (selectedSlot.TimeSlot || "")
                                            .toLowerCase()
                                            .includes("not interested");

                                        if (isNotInterested) return null;

                                        return (
                                          <motion.div
                                            initial={{ opacity: 0, x: 10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            className="flex items-center gap-3"
                                          >
                                            <div className="flex items-center gap-1.5 whitespace-nowrap">
                                              <span className="text-[12px] font-medium text-gray-400">
                                                Available:
                                              </span>
                                              {slotState.loading ? (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-600 shrink-0" />
                                              ) : (
                                                <span className="text-[12px] font-bold text-orange-600">
                                                  {slotState.seats}
                                                </span>
                                              )}
                                            </div>
                                          </motion.div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      })()}

                      {/* Visitor Selection Breakdown */}
                      <div className="space-y-3 border-t border-orange-100 border-dashed">
                        {[
                          {
                            type: "Adult",
                            rate: item.PerPersonRate,
                            label: "per Adult",
                          },
                          {
                            type: "Child7-15",
                            rate: item.ChildSevenYrRate,
                            label: "per Child (3-15)Yr",
                          },
                          {
                            type: "Child3-6",
                            rate: item.ChildThreeYrRate,
                            label: "per Child (3-6)Yr",
                          },
                        ]
                          .filter((cat) => cat.rate > 0)
                          .map((cat: any) => (
                            <div
                              key={cat.type}
                              className="flex flex-row items-center justify-between gap-2 py-2 border-b border-orange-50/50 last:border-0"
                            >
                              {/* Left side: Icon + Rate Info */}
                              <div className="flex items-center gap-2 sm:gap-3 shrink-0">
                                <div className="hidden sm:flex w-8 h-8 rounded-full bg-orange-50 items-center justify-center text-orange-600 shrink-0">
                                  <Users className="w-4 h-4" />
                                </div>
                                <div className="min-w-0">
                                  <p className="text-[9px] sm:text-[11px] text-gray-400 font-bold uppercase tracking-wider leading-none mb-1 whitespace-nowrap">
                                    {cat.label}
                                  </p>
                                  <p className="text-xs sm:text-sm font-bold text-gray-900 leading-none whitespace-nowrap">
                                    ₹ {formatIndianNumber(cat.rate || 0)}
                                  </p>
                                </div>
                              </div>

                              {/* Counter & Subtotal */}
                              <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 md:flex-1">
                                <div className="flex items-center gap-1 sm:gap-3 bg-white p-0.5 sm:p-1 rounded-xl sm:rounded-2xl border border-orange-100 shadow-xs">
                                  <button
                                    disabled={
                                      (cat.type === "Adult"
                                        ? item.TotalAdults || 0
                                        : cat.type === "Child7-15"
                                          ? item.TotalChildSevenYr || 0
                                          : item.TotalChildThreeYr || 0) <= 0
                                    }
                                    onClick={() =>
                                      handleCountChange(
                                        item.CartID,
                                        cat.type as any,
                                        -1,
                                      )
                                    }
                                    className="w-6 h-6 sm:w-8 sm:h-8 rounded bg-orange-50 text-orange-600 flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all active:scale-90 cursor-pointer shadow-sm shrink-0 disabled:opacity-30 disabled:cursor-not-allowed"
                                  >
                                    <Minus className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
                                  </button>
                                  <span className="w-5 sm:w-6 text-center text-xs sm:text-sm font-bold text-gray-800">
                                    {cat.type === "Adult"
                                      ? item.TotalAdults || 0
                                      : cat.type === "Child7-15"
                                        ? item.TotalChildSevenYr || 0
                                        : item.TotalChildThreeYr || 0}
                                  </span>
                                  <button
                                    onClick={() =>
                                      handleCountChange(
                                        item.CartID,
                                        cat.type as any,
                                        1,
                                      )
                                    }
                                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded bg-orange-50 text-orange-600 flex items-center justify-center hover:bg-orange-500 hover:text-white transition-all active:scale-90 cursor-pointer shadow-sm shrink-0 ${
                                      (cat.type === "Adult" &&
                                        (item.TotalAdults || 0) >=
                                          MAX_ADULT_TICKETS) ||
                                      (cat.type === "Child7-15" &&
                                        (item.TotalChildSevenYr || 0) >=
                                          MAX_CHILD_TICKETS)
                                        ? "opacity-30"
                                        : ""
                                    }`}
                                  >
                                    <Plus className="w-2.5 h-2.5 sm:w-3.5 sm:h-3.5" />
                                  </button>
                                </div>

                                {/* Price Total */}
                                <div className="text-right shrink-0 min-w-[65px] sm:min-w-[90px]">
                                  <p className="text-xs sm:text-base font-bold text-gray-900 whitespace-nowrap">
                                    ₹{" "}
                                    {formatIndianNumber(
                                      (cat.type === "Adult"
                                        ? item.TotalAdults || 0
                                        : cat.type === "Child7-15"
                                          ? item.TotalChildSevenYr || 0
                                          : item.TotalChildThreeYr || 0) *
                                        cat.rate,
                                    )}
                                  </p>
                                </div>
                              </div>
                            </div>
                          ))}
                      </div>

                      {/* Combined Summary Bar */}
                      {(() => {
                        const attractions = item.AttractionTicket || [];

                        let minAvailable = 50;
                        let isSlotLoading = false;
                        let hasVG = false;
                        let vgSelected = false;

                        for (const attr of attractions) {
                          const slotId =
                            availability[item.CartID]?.[attr.ID]?.id;
                          const avail =
                            slotId &&
                            slotId > 0 &&
                            availability[item.CartID]?.[attr.ID]?.seats !==
                              undefined
                              ? availability[item.CartID][attr.ID].seats
                              : 50;

                          const loading =
                            !!availability[item.CartID]?.[attr.ID]?.loading;
                          if (loading) {
                            isSlotLoading = true;
                          }

                          const hasServerNotInterested = (
                            attr.AttractionTicketChild || []
                          ).some((slot: any) =>
                            (slot.TimeSlot || "")
                              .toLowerCase()
                              .includes("not interested"),
                          );
                          const isRequired = !hasServerNotInterested;

                          if (isRequired) {
                            vgSelected = (hasVG ? vgSelected : true) && !!slotId;
                            hasVG = true;
                            minAvailable = Math.min(
                              minAvailable,
                              Math.max(0, avail),
                            );
                          } else if (slotId && slotId > 0) {
                            const selectedSlot = (
                              attr.AttractionTicketChild || []
                            ).find((s: any) => s.ID === slotId);
                            const isNotInterestedSelected =
                              slotId === 0 ||
                              (selectedSlot &&
                                (selectedSlot.TimeSlot || "")
                                  .toLowerCase()
                                  .includes("not interested"));

                            if (!isNotInterestedSelected) {
                              minAvailable = Math.min(
                                minAvailable,
                                Math.max(0, avail),
                              );
                            }
                          }
                        }

                        const currentTotal =
                          (item.TotalAdults || 0) +
                          (item.TotalChildSevenYr || 0) +
                          (item.TotalChildThreeYr || 0);

                        const showLimitMsg =
                          (!hasVG || vgSelected) &&
                          !isSlotLoading &&
                          minAvailable > 0;
                        const isExceeded =
                          (!hasVG || vgSelected) &&
                          !isSlotLoading &&
                          currentTotal > minAvailable;

                        return (
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-4 border-t border-orange-100 mt-2">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-3 flex-wrap">
                                <span className="text-[10px] sm:text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                                  Total Tickets:
                                </span>
                                <span
                                  className={`text-lg sm:text-xl font-bold ${
                                    isExceeded
                                      ? "text-red-600"
                                      : "text-orange-600"
                                  }`}
                                >
                                  {currentTotal}
                                </span>
                                {isExceeded && (
                                  <span className="text-[10px] bg-red-100 text-red-600 font-bold px-2 py-0.5 rounded-full uppercase tracking-wider">
                                    Exceeds Available ({minAvailable})
                                  </span>
                                )}
                              </div>
                              {showLimitMsg && (
                                <p
                                  className={`text-[9px] font-medium uppercase tracking-widest flex items-center gap-1.5 ${
                                    isExceeded
                                      ? "text-red-500 font-bold"
                                      : "text-gray-400"
                                  }`}
                                >
                                  <span
                                    className={`w-1 h-1 rounded-full ${
                                      isExceeded ? "bg-red-500" : "bg-gray-300"
                                    }`}
                                  ></span>
                                  Maximum limit:{" "}
                                  {minAvailable < 50
                                    ? `${minAvailable} tickets available in selected slot(s)`
                                    : `${MAX_ADULT_TICKETS} adults & ${MAX_CHILD_TICKETS} children per attraction`}
                                </p>
                              )}
                            </div>

                            <div className="flex flex-col items-end gap-0.5">
                              <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-6 flex-wrap justify-end">
                                <span className="text-[10px] sm:text-xs font-semibold text-gray-400 tracking-wide whitespace-nowrap">
                                  Item Sub Total:
                                </span>
                                <span className="text-lg sm:text-xl font-bold text-orange-600 whitespace-nowrap">
                                  ₹ {formatIndianNumber(item.Total || 0)}
                                </span>
                              </div>
                              {(item.CalculatedServiceCharge || 0) > 0 && (
                                <span className="text-[10px] text-gray-400 italic font-medium">
                                  + ₹{" "}
                                  {formatIndianNumber(
                                    item.CalculatedServiceCharge,
                                  )}{" "}
                                  Service Charge
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })()}
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            ) : (
              <div className="p-8 sm:p-16 text-center space-y-4 bg-white rounded-2xl sm:rounded-3xl border border-dashed border-gray-200">
                <ShoppingCart className="w-10 h-10 sm:w-12 sm:h-12 text-gray-100 mx-auto" />
                <p className="text-gray-500 text-sm sm:text-base font-semibold italic">
                  Your cart is feeling light...
                </p>
                <button
                  onClick={() => (window.location.href = "/sou-tickets")}
                  className="px-6 sm:px-8 py-2.5 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-md shadow-md hover:scale-[1.02] transition-all cursor-pointer text-sm"
                >
                  Add Attractions
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {/* Hotel Review Section */}
      {hotelCart && hotelCart.length > 0 && (
        <div className="space-y-4 sm:space-y-6">
          <div className="flex items-center gap-3">
            <div className="h-8 w-1 bg-orange-500 rounded-full"></div>
            <h2 className="text-lg sm:text-xl font-bold text-gray-900">
              Review your Hotel
            </h2>
          </div>

          {hotelCart.map((hotel, idx) => (
            <HotelCard
              key={hotel.CartID || idx}
              hotel={hotel}
              hasDateError={failedHotelDates.includes(hotel.CartID)}
              onRemove={(id) =>
                setConfirmDelete({
                  isOpen: true,
                  cartId: id,
                  cartType: "Hotel",
                })
              }
              onUpdate={(data) => handleHotelUpdate(hotel.CartID, data)}
            />
          ))}
        </div>
      )}

      {/* Footer Summary */}
      <div className="space-y-6 sm:space-y-8 pt-6 sm:pt-8 pb-12 sm:pb-16">
        <div className="flex flex-col items-center gap-2">
          <span className="text-[10px] sm:text-[12px] font-semibold text-gray-400 tracking-[0.3em] text-center">
            {hasUnavailableHotelRooms ? "Total" : "Total Amount Payable"}
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-semibold text-gray-900 tracking-tight text-center">
            ₹ {formatIndianNumber(calculateSubtotal() || 0)}
          </h2>
        </div>

        <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 w-full max-w-3xl mx-auto px-4">
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-[#343F4D] text-white font-semibold rounded-2xl shadow-md hover:bg-[#2A333E] hover:scale-[1.02] active:scale-95 transition-all cursor-pointer text-sm"
          >
            <Plus className="w-4 h-4" />
            <span>Add Attractions</span>
          </button>
          {hotelCart && !hotelCart.length && (
            <button
              onClick={async () => {
                if (localCartItems.length > 0 && !selectedDate) {
                  toast.error("Please select a visit date first", {
                    icon: "📅",
                  });
                  triggerDateHighlight();
                  const header = document.querySelector(
                    ".header-date-selector",
                  );
                  header?.scrollIntoView({
                    behavior: "smooth",
                    block: "center",
                  });
                  return;
                }

                let targetDate = selectedDate;
                if (hotelCart && hotelCart.length > 0) {
                  const firstHotel = hotelCart[0];
                  const liveUpdate = hotelUpdates[firstHotel.CartID];
                  if (liveUpdate && liveUpdate.checkIn) {
                    targetDate = liveUpdate.checkIn;
                  } else if (firstHotel.CheckIn) {
                    targetDate = firstHotel.CheckIn;
                  }
                }

                const dateParam = targetDate
                  ? formatDateForAPI(targetDate)
                  : undefined;
                if (
                  activeHotels.length === 0 ||
                  lastFetchedHotelsDate.current !== dateParam
                ) {
                  dispatch(setLoading(true));
                  try {
                    const hotelsData = await fetchHotelsForCartModal(dateParam);
                    setActiveHotels(hotelsData);
                    lastFetchedHotelsDate.current = dateParam || "";
                    setIsHotelModalOpen(true);
                  } catch (error) {
                    console.error("Error fetching hotels:", error);
                    toast.error("Failed to load hotels. Please try again.");
                  } finally {
                    dispatch(setLoading(false));
                  }
                } else {
                  setIsHotelModalOpen(true);
                }
              }}
              className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-3 bg-white text-orange-600 border border-orange-500 hover:bg-orange-50 cursor-pointer text-sm font-semibold rounded-2xl shadow-sm hover:scale-[1.02] active:scale-95 transition-all"
            >
              <Plus className="w-4 h-4" />
              <span>Add Hotels</span>
            </button>
          )}
          <button
            onClick={handleFinalContinue}
            className="flex-1 flex items-center justify-center gap-2 px-6 sm:px-8 py-3 bg-gradient-to-r from-orange-500 to-amber-600 text-white font-semibold rounded-2xl shadow-lg hover:scale-[1.02] active:scale-95 transition-all group cursor-pointer text-sm"
          >
            <span>Proceed</span>
            <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>

      <AttractionModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        tickets={activeTickets}
        cartItems={localCartItems}
        onAdd={handleAddFromModal}
        onRemove={(ticketId: number) => {
          const item = localCartItems.find((it) => it.TicketID === ticketId);
          if (item)
            setConfirmDelete({
              isOpen: true,
              cartId: item.CartID,
              cartType: "Ticket",
            });
        }}
      />
      <HotelModal
        isOpen={isHotelModalOpen}
        onClose={() => setIsHotelModalOpen(false)}
        hotels={activeHotels}
        hotelCart={hotelCart}
        onAdd={handleAddHotelFromModal}
      />
      <ConfirmationModal
        isOpen={confirmDelete.isOpen}
        onClose={() =>
          setConfirmDelete({ isOpen: false, cartId: null, cartType: "Ticket" })
        }
        onConfirm={handleRemoveFromCart}
        title="Remove Attraction?"
        message="Are you sure you want to remove this attraction from your booking? This cannot be undone."
        confirmLabel="Yes, Remove"
      />
    </div>
  );
}
